import React, { useEffect, useState } from "react";
import { ChevronDown, Plus, Trash2, Terminal, Info, Settings2, Play, Eye, EyeOff, Search } from "lucide-react";
import axios from "axios";
import { Environment as environment } from "../../environment.ts";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import VariablePicker from "../Common/VariablePicker.tsx";
import ReactJson from 'react-json-view';

interface TabProps {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}

const Tab: React.FC<TabProps> = ({ active, onClick, children }) => (
    <button
        onClick={onClick}
        type="button"
        className={`api-pill-tab ${active ? 'active' : ''}`}
    >
        {children}
    </button>
);

interface ApiRequestDialogProps {
    data: any; // Contains nodeId and subNodeId
    onClose: (event: any) => void;
}

const ApiRequestDialog: React.FC<ApiRequestDialogProps> = ({ data, onClose }) => {
    const { nodes, setNodes, businessId } = useFlowStore();
    const [url, setURL] = useState("");
    const [requestMethod, setRequestMethod] = useState("GET");
    const [headers, setHeaders] = useState<{ key: string; value: string }[]>([{ key: "", value: "" }]);
    const [params, setParams] = useState<{ key: string; value: string }[]>([{ key: "", value: "" }]);
    const [body, setBody] = useState("");
    const [activeTab, setActiveTab] = useState("headers");
    const [isLoading, setIsLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState<any>(null);
    const [showResponse, setShowResponse] = useState(false);
    const [showPathPicker, setShowPathPicker] = useState(false);
    const [pickerTarget, setPickerTarget] = useState<number | null>(null);
    const [attributeMapping, setAttributeMapping] = useState<{ attributeId: string; responsePath: string }[]>([]);
    const [availableAttributes, setAvailableAttributes] = useState<any[]>([]);

    const getAllAttributes = () => {
        if (!businessId) return;
        const payload = {
            businessId: businessId,
            page: 0,
            size: 500,
            templateStatus: null,
            searchText: ''
        };
        axios.put(`${environment.baseUrl}/fetch/attribute`, payload).then((response) => {
            const dataAttrs = response.data?.data?.data || response.data?.data || [];
            setAvailableAttributes(dataAttrs);
        }).catch((error) => { console.error("Error fetching attributes", error); });
    };

    const flattenPaths = (obj: any, prefix = ''): string[] => {
        let paths: string[] = [];
        if (typeof obj !== 'object' || obj === null) return paths;

        if (Array.isArray(obj)) {
            obj.forEach((item, index) => {
                const newPrefix = prefix ? `${prefix}.${index}` : `${index}`;
                paths.push(newPrefix);
                if (typeof item === 'object' && item !== null) {
                    paths = paths.concat(flattenPaths(item, newPrefix));
                }
            });
        } else {
            for (const key in obj) {
                const newPrefix = prefix ? `${prefix}.${key}` : key;
                paths.push(newPrefix);
                if (typeof obj[key] === 'object' && obj[key] !== null) {
                    paths = paths.concat(flattenPaths(obj[key], newPrefix));
                }
            }
        }
        return paths;
    };

    const responsePaths = apiResponse ? flattenPaths(apiResponse) : [];

    useEffect(() => {
        const node = nodes.find(n => n.data.id === data.nodeId);
        if (node) {
            const content = (node.data as any).content.find((c: any) => c.id === data.subNodeId);
            if (content && content.data) {
                const ro = content.data.requestObject || {};
                setURL(ro.url || content.data.url || "");
                setRequestMethod(ro.method || content.data.method || "GET");
                setHeaders(ro.headers?.length ? ro.headers : (content.data.headers?.length ? content.data.headers : [{ key: "", value: "" }]));
                
                let rawBody = ro.payload || content.data.body || content.data.payload || "";
                if (typeof rawBody === 'object' && rawBody !== null) {
                    try {
                        setBody(JSON.stringify(rawBody, null, 2));
                    } catch (e) {
                        setBody(String(rawBody));
                    }
                } else {
                    setBody(String(rawBody));
                }

                setParams(ro.params?.length ? ro.params : (content.data.params?.length ? content.data.params : [{ key: "", value: "" }]));
                
                const mappings = content.data.attributeMapping || content.data.attributeMappings || content.data.capturingAttributes || ro.attributeMapping || ro.attributeMappings || ro.capturingAttributes || [];
                
                let finalMappings: { attributeId: string; responsePath: string }[] = [];
                if (Array.isArray(mappings)) {
                    finalMappings = mappings.map((m: any) => ({
                        attributeId: m.attributeId || m.value || "",
                        responsePath: m.responsePath || m.key || ""
                    }));
                }
                setAttributeMapping(finalMappings);
            }
        }
        getAllAttributes();
    }, [data.nodeId, data.subNodeId, nodes]);

    const handleGetResponse = async () => {
        setIsLoading(true);
        try {
            const headerObj = headers.reduce((acc: any, curr) => {
                if (curr.key) acc[curr.key] = curr.value;
                return acc;
            }, {});

            const res = await axios({
                method: requestMethod,
                url,
                data: requestMethod !== "GET" ? JSON.parse(body || "{}") : undefined,
                headers: headerObj,
                params: params.reduce((acc: any, curr) => {
                    if (curr.key) acc[curr.key] = curr.value;
                    return acc;
                }, {})
            });
            setApiResponse(res.data);
            setShowResponse(true);
        } catch (error: any) {
            setApiResponse(error.response?.data || { error: error.message });
            setShowResponse(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = () => {
        let finalBody = body;
        if (requestMethod !== 'GET' && body.trim()) {
            finalBody = body.replace(/(?<!["'])(\{\{[^{}]+\}\})(?!["'])/g, '"$1"');
        }

        const updatedNodes = nodes.map((node) => {
            if (node.data.id === data.nodeId) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        content: (node.data as any).content.map((c: any) => {
                            if (c.id === data.subNodeId) {
                                return {
                                    ...c,
                                    data: {
                                        ...c.data, 
                                        requestObject: {
                                            url,
                                            method: requestMethod,
                                            payload: finalBody,
                                            headers: headers.filter(h => h.key),
                                            params: params.filter(p => p.key),
                                        },
                                        attributeMapping: attributeMapping,
                                        capturingAttributes: attributeMapping.map(attr => ({
                                            id: crypto.randomUUID(),
                                            key: attr.responsePath,
                                            value: attr.attributeId
                                        }))
                                    }
                                };
                            }
                            return c;
                        })
                    }
                };
            }
            return node;
        });
        setNodes(updatedNodes as any);
        onClose(null);
    };

    return (
        <div className="api-dialog-content-wrapper" onClick={() => setShowPathPicker(false)}>
            <div className="api-dialog-scroll-content">
                <div className="api-config-section">
                    <div className="api-request-row">
                        <div className="api-method-select-wrapper">
                            <label className="label-premium">Method</label>
                            <select 
                                className="api-method-select premium-input" 
                                value={requestMethod} 
                                onChange={(e) => setRequestMethod(e.target.value)}
                            >
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                                <option value="PUT">PUT</option>
                                <option value="DELETE">DELETE</option>
                                <option value="PATCH">PATCH</option>
                            </select>
                            <ChevronDown className="api-select-icon" size={16} />
                        </div>
                        <div className="api-url-input-wrapper">
                            <label className="label-premium">Endpoint URL</label>
                            <input
                                type="text"
                                className="api-url-input premium-input"
                                placeholder="https://api.example.com/resource"
                                value={url}
                                onChange={(e) => setURL(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="api-pill-tabs">
                        <Tab active={activeTab === 'headers'} onClick={() => setActiveTab('headers')}>Headers</Tab>
                        <Tab active={activeTab === 'body'} onClick={() => setActiveTab('body')}>Request Body</Tab>
                        <Tab active={activeTab === 'params'} onClick={() => setActiveTab('params')}>Query Params</Tab>
                    </div>

                    <div className="api-tab-content">
                        {activeTab === 'headers' && (
                            <div className="space-y-3">
                                {headers.map((header, idx) => (
                                    <div key={idx} className="api-param-row">
                                        <input className="premium-input flex-1" placeholder="Key" value={header.key} onChange={e => {
                                            const newH = [...headers]; newH[idx].key = e.target.value; setHeaders(newH);
                                        }} />
                                        <div className="flex-1 flex items-center gap-2">
                                            <input className="premium-input flex-1" placeholder="Value" value={header.value} onChange={e => {
                                                const newH = [...headers]; newH[idx].value = e.target.value; setHeaders(newH);
                                            }} />
                                            <VariablePicker label="" onSelect={(val) => {
                                                const newH = [...headers]; newH[idx].value = (newH[idx].value || "") + `{{${val}}}`; setHeaders(newH);
                                            }} />
                                        </div>
                                        <button onClick={() => setHeaders(headers.filter((_, i) => i !== idx))} className="p-2 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                                    </div>
                                ))}
                                <button onClick={() => setHeaders([...headers, { key: "", value: "" }])} className="api-outline-button flex items-center gap-1"><Plus size={14} /> Add Header</button>
                            </div>
                        )}

                        {activeTab === 'body' && (
                            <div className="space-y-3">
                                <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg border border-slate-200">
                                    <span className="text-[10px] font-bold text-slate-500 px-2">JSON PAYLOAD</span>
                                    <VariablePicker onSelect={(val) => setBody(prev => prev + `{{${val}}}`)} />
                                </div>
                                <textarea 
                                    className="api-body-textarea premium-input font-mono text-xs" 
                                    placeholder='{ "key": "value" }' 
                                    value={body}
                                    onChange={e => setBody(e.target.value)}
                                />
                            </div>
                        )}

                        {activeTab === 'params' && (
                            <div className="space-y-3">
                                {params.map((param, idx) => (
                                    <div key={idx} className="api-param-row">
                                        <input className="premium-input flex-1" placeholder="Key" value={param.key} onChange={e => {
                                            const newP = [...params]; newP[idx].key = e.target.value; setParams(newP);
                                        }} />
                                        <div className="flex-1 flex items-center gap-2">
                                            <input className="premium-input flex-1" placeholder="Value" value={param.value} onChange={e => {
                                                const newP = [...params]; newP[idx].value = e.target.value; setParams(newP);
                                            }} />
                                            <VariablePicker label="" onSelect={(val) => {
                                                const newP = [...params]; newP[idx].value = (newP[idx].value || "") + `{{${val}}}`; setParams(newP);
                                            }} />
                                        </div>
                                        <button onClick={() => setParams(params.filter((_, i) => i !== idx))} className="p-2 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                                    </div>
                                ))}
                                <button onClick={() => setParams([...params, { key: "", value: "" }])} className="api-outline-button flex items-center gap-1"><Plus size={14} /> Add Param</button>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-center mt-4">
                         <button 
                            className="test-request-btn"
                            onClick={handleGetResponse}
                            disabled={isLoading}
                        >
                            {isLoading ? <div className="spinner-mini" /> : <Play size={14} />}
                            {isLoading ? 'Testing...' : 'Test API Request'}
                        </button>
                    </div>
                </div>

                <div className="my-10 border-t border-slate-100" />

                {apiResponse && showResponse && (
                    <div className="api-response-preview-container mb-8">
                        <div className="flex justify-between items-center mb-2 px-1">
                            <span className="api-section-label flex items-center gap-2">
                                <Terminal size={14} className="text-slate-400" />
                                Test Response
                            </span>
                            <button onClick={() => setShowResponse(false)} className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-1">
                                <EyeOff size={12} /> Hide Response
                            </button>
                        </div>
                        <div className="api-response-viewer premium-card">
                            <ReactJson 
                                src={apiResponse} 
                                theme="harmonic" 
                                collapsed={1} 
                                name={false} 
                                displayDataTypes={false}
                                enableClipboard={true}
                                style={{ padding: '16px', fontSize: '11px' }}
                            />
                        </div>
                    </div>
                )}

                <div className="api-capture-section">
                    <div className="api-section-header">
                        <span className="api-section-label flex items-center gap-2">
                            <Info size={14} className="text-[var(--primary-bg-color)]" />
                            Response Capture
                        </span>
                    </div>
                    
                    <div className="space-y-4">
                        {attributeMapping.map((mapping, idx) => {
                            // Filter paths based on current input text if picker is open for this target
                            const filteredPaths = showPathPicker && pickerTarget === idx
                                ? responsePaths.filter(p => !mapping.responsePath || p.toLowerCase().includes(mapping.responsePath.toLowerCase()))
                                : [];

                            return (
                                <div key={idx} className="api-param-row">
                                    <select 
                                        className="premium-input flex-1" 
                                        value={mapping.attributeId}
                                        onChange={e => {
                                            const newM = [...attributeMapping];
                                            newM[idx].attributeId = e.target.value;
                                            setAttributeMapping(newM);
                                        }}
                                    >
                                        <option value="">Map To Attribute...</option>
                                        {availableAttributes.map(attr => (
                                            <option key={attr.attributeId} value={attr.attributeId}>{attr.attributeName}</option>
                                        ))}
                                    </select>
                                    <div className="flex-1 relative" onClick={e => e.stopPropagation()}>
                                        <div className="relative">
                                            <input 
                                                className="premium-input w-full pr-8" 
                                                placeholder="Response Path (e.g. data.id)" 
                                                value={mapping.responsePath}
                                                onFocus={() => { setPickerTarget(idx); setShowPathPicker(true); }}
                                                onChange={e => {
                                                    const newM = [...attributeMapping];
                                                    newM[idx].responsePath = e.target.value;
                                                    setAttributeMapping(newM);
                                                    setPickerTarget(idx);
                                                    setShowPathPicker(true);
                                                }}
                                            />
                                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                                {apiResponse && (
                                                    <button onClick={() => { setPickerTarget(idx); setShowPathPicker(!showPathPicker); }} className="text-slate-300 hover:text-[var(--primary-bg-color)]">
                                                        <Search size={14} />
                                                    </button>
                                                )}
                                                <ChevronDown size={14} className="text-slate-300" />
                                            </div>
                                        </div>
                                        
                                        {showPathPicker && pickerTarget === idx && apiResponse && (
                                            <div className="api-path-dropdown premium-card shadow-2xl border border-slate-200">
                                                <div className="api-path-list">
                                                    {filteredPaths.length > 0 ? filteredPaths.map(path => (
                                                        <div key={path} className="picker-item" onClick={() => {
                                                            const newM = [...attributeMapping];
                                                            newM[idx].responsePath = path;
                                                            setAttributeMapping(newM);
                                                            setShowPathPicker(false);
                                                        }}>
                                                            {path}
                                                        </div>
                                                    )) : (
                                                        <div className="api-path-empty">
                                                            {responsePaths.length === 0 ? "Run Test Request first" : "No matching paths found"}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={() => setAttributeMapping(attributeMapping.filter((_, i) => i !== idx))} className="p-2 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                                </div>
                            );
                        })}
                        <button onClick={() => setAttributeMapping([...attributeMapping, { attributeId: "", responsePath: "" }])} className="api-outline-button flex items-center gap-1"><Plus size={14} /> Map Variable</button>
                    </div>
                </div>
            </div>

            <div className="api-dialog-footer">
                <button className="save-config-btn" onClick={handleSave}>
                    Save Configuration
                </button>
            </div>

            <style>{`
                .api-dialog-content-wrapper {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }
                .api-dialog-scroll-content {
                    flex: 1;
                    overflow-y: auto;
                    padding: 24px;
                    max-height: 70vh;
                }
                .api-section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    padding-bottom: 8px;
                    border-bottom: 1px solid #f1f5f9;
                }
                .api-section-label {
                    font-size: 11px;
                    font-weight: 700;
                    color: #475569;
                    text-transform: uppercase;
                    letter-spacing: 0.1em;
                }
                .api-request-row {
                    display: flex;
                    gap: 12px;
                    margin-bottom: 24px;
                    align-items: flex-end;
                }
                .api-method-select-wrapper {
                    width: 120px;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    position: relative;
                }
                .api-select-icon {
                    position: absolute;
                    right: 12px;
                    bottom: 10px;
                    pointer-events: none;
                    color: #94a3b8;
                }
                .api-url-input-wrapper {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    position: relative;
                }
                .api-method-select, .api-url-input {
                    width: 100%;
                    outline: none;
                }
                .api-pill-tabs {
                    display: flex;
                    gap: 4px;
                    padding: 4px;
                    border-radius: 12px;
                    margin: 16px 0 24px 0;
                    background: #f1f5f9;
                }
                .api-pill-tab {
                    flex: 1;
                    padding: 8px 12px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #64748b;
                    border-radius: 8px;
                    transition: all 0.2s;
                    border: none;
                    background: transparent;
                    cursor: pointer;
                }
                .api-pill-tab.active {
                    background: #ffffff;
                    color: var(--primary-bg-color);
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }
                .api-tab-content {
                    min-height: 180px;
                    margin-bottom: 20px;
                }
                .api-param-row {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    margin-bottom: 12px;
                }
                .api-body-textarea {
                    width: 100%;
                    min-height: 180px;
                    resize: vertical;
                    line-height: 1.6;
                    padding: 12px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }
                .api-response-viewer {
                    min-height: 150px;
                    max-height: 350px;
                    overflow: auto;
                    border-radius: 12px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                }
                .api-path-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.15);
                    z-index: 10001;
                    margin-top: 6px;
                    border: 1px solid #e2e8e0;
                    animation: slideDown 0.2s ease-out;
                }
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .api-path-list {
                    max-height: 250px;
                    overflow-y: auto;
                    padding: 6px;
                }
                .api-path-empty {
                    padding: 20px;
                    text-align: center;
                    font-size: 11px;
                    color: #94a3b8;
                    font-style: italic;
                }
                .picker-item {
                    padding: 8px 12px;
                    font-size: 12px;
                    color: #475569;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.15s;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .picker-item:hover {
                    background: #f0f9ff;
                    color: var(--primary-bg-color);
                }
                .api-outline-button {
                    background: white;
                    border: 1px solid #e2e8f0;
                    color: #64748b;
                    font-size: 11px;
                    font-weight: 600;
                    padding: 4px 12px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .api-outline-button:hover {
                    border-color: var(--primary-bg-color);
                    color: var(--primary-bg-color);
                    background: rgba(10, 71, 76, 0.05);
                }
                .test-request-btn {
                    background: var(--primary-bg-color);
                    color: white;
                    padding: 10px 24px;
                    border-radius: 9999px;
                    font-weight: 700;
                    font-size: 13px;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.2s;
                    box-shadow: 0 4px 10px rgba(10, 71, 76, 0.2);
                }
                .test-request-btn:hover {
                    background: #073539;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 15px rgba(10, 71, 76, 0.3);
                }
                .test-request-btn:disabled {
                    background: #94a3b8;
                    cursor: not-allowed;
                    box-shadow: none;
                    transform: none;
                }
                .api-dialog-footer {
                    padding: 16px 24px;
                    border-top: 1px solid #f1f5f9;
                    display: flex;
                    justify-content: flex-end;
                    background: #f8fafc;
                }
                .save-config-btn {
                    background: var(--primary-bg-color);
                    color: white;
                    padding: 12px 40px;
                    border-radius: 8px;
                    font-weight: 700;
                    font-size: 15px;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: 0 4px 6px -1px rgba(10, 71, 76, 0.2);
                }
                .save-config-btn:hover {
                    background: #073539;
                    box-shadow: 0 10px 15px -3px rgba(10, 71, 76, 0.3);
                    transform: translateY(-1px);
                }
                .spinner-mini {
                    width: 14px;
                    height: 14px;
                    border: 2px solid rgba(255,255,255,0.3);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default ApiRequestDialog;
